import React, { memo, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import styles from "./Sidebar.module.css";
import { useSelector } from "react-redux";
import { useAuth } from "../../contexts/JWTContext";

const Sidebar = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const websiteSetting = useSelector((state) => state.user.websiteSetting);
  const currentUser = useSelector((state) => state.user.currentUser);
  const playerBalance = useSelector((state) => state.user.playerBalance);
  const wheelPoint = useSelector((state) => state.user.wheelPoint);
  const { logout, isAuthenticated } = useAuth();
  const settings = Array.isArray(websiteSetting)
    ? websiteSetting[0]
    : websiteSetting?.data?.[0] || {};
  const apiBaseUrl = (process.env.REACT_APP_API_BASE_URL || "").replace(
    /\/$/,
    "",
  );

  const resolveIconSrc = (path) => {
    if (!path) return "";
    if (/^https?:/i.test(path)) return path;
    return `${apiBaseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
  };

  const renderSidebarIcon = (customPath, fallbackIcon, alt) => {
    const src = resolveIconSrc(customPath);
    if (src) {
      return <img src={src} alt={alt} className={styles.iconImage} />;
    }
    return <Icon icon={fallbackIcon} className={styles.icon} />;
  };

  const handleLogout = () => {
    try {
      logout();
    } catch (e) {
      console.error("Logout error:", e);
    }
    try {
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("token");
      localStorage.removeItem("authToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("currentUser");
      localStorage.removeItem("persist:root");
      sessionStorage.clear();
    } catch (e) {
      // ignore
    }
    window.location.href = "/";
  };

  const handlePromotionClick = useCallback(() => {
    navigate(isAuthenticated ? "/menu?tab=autoPromo" : "/promotion");
  }, [isAuthenticated, navigate]);

  const isPromotionActive =
    location.pathname === "/promotion" ||
    (location.pathname === "/menu" &&
      new URLSearchParams(location.search).get("tab") === "autoPromo");

  const menuItems = [
    {
      path: "/menu",
      title: "กระเป๋า",
      icon: "material-symbols:home",
      customIcon: settings?.footerHomeIconUrl,
    },
    {
      path: isAuthenticated ? "/menu?tab=autoPromo" : "/promotion",
      title: "โปรโมชั่น",
      icon: "material-symbols:featured-seasonal-and-gifts-rounded",
      customIcon: settings?.headerPromotionIconUrl,
      onClick: handlePromotionClick,
      isActive: isPromotionActive,
    },
    {
      path: "/livecasino",
      title: "ออนไลน์",
      icon: "cil:casino",
      customIcon: settings?.sidebarCasinoIconUrl,
    },
    {
      path: "/slot",
      title: "เกมส์",
      icon: "mdi:slot-machine",
      customIcon: settings?.sidebarSlotIconUrl,
    },
    {
      path: "/sportbook",
      title: "กีฬา",
      icon: "emojione-monotone:soccer-ball",
      customIcon: settings?.sidebarSportIconUrl,
    },
    {
      path: "/card",
      title: "ยิงปลา",
      icon: "game-icons:spearfishing",
      customIcon: settings?.sidebarFishIconUrl,
    },
    {
      path: "/reviews",
      title: "รีวิว",
      icon: "fluent-color:clipboard-text-edit-20",
    },
  ];

  return (
    <aside className={styles.sidebar}>
      {currentUser ? (
        <div className={styles.userPanel}>
          <div className={styles.userTop}>
            <div className={styles.userAvatar}>
              <Icon
                icon="solar:user-circle-bold-duotone"
                width={28}
                height={28}
              />
            </div>
            <div className={styles.userName}>
              {currentUser?.username || currentUser?.name || "ผู้ใช้"}
            </div>
          </div>

          <div className={styles.userStats}>
            <div className={styles.statChip}>
              <div className={`${styles.statIconBox} ${styles.balanceIcon}`}>
                <Icon
                  icon="solar:wallet-money-bold-duotone"
                  width={16}
                  height={16}
                />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>เครดิต</span>
                <span className={styles.statValue}>
                  {Number(playerBalance || 0).toLocaleString()}
                </span>
              </div>
            </div>

            <div className={styles.statChip}>
              <div className={`${styles.statIconBox} ${styles.pointIcon}`}>
                <Icon icon="ri:copper-diamond-fill" width={16} height={16} />
              </div>
              <div className={styles.statInfo}>
                <span className={styles.statLabel}>เพชร</span>
                <span className={styles.statValue}>
                  {Number(wheelPoint || 0).toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          <button
            className={styles.logoutBtnSidebar}
            onClick={handleLogout}
            aria-label="Logout"
          >
            <Icon icon="solar:logout-2-bold-duotone" width={16} height={16} />
            ออกจากระบบ
          </button>
        </div>
      ) : null}

      <nav className={styles.nav}>
        <ul className={styles.menuList}>
          {menuItems.map((item) => (
            <li key={item.path} className={styles.menuItem}>
              {item.onClick ? (
                <button
                  type="button"
                  className={`${styles.menuLink} ${
                    item.isActive ? styles.active : ""
                  } ${item.customIcon ? styles.menuLinkImage : ""}`}
                  title={item.title}
                  onClick={item.onClick}
                >
                  {renderSidebarIcon(item.customIcon, item.icon, item.title)}
                  <span className={styles.title}>{item.title}</span>
                </button>
              ) : (
                <NavLink
                  to={item.path}
                  className={({ isActive }) =>
                    `${styles.menuLink} ${isActive ? styles.active : ""} ${
                      item.customIcon ? styles.menuLinkImage : ""
                    }`
                  }
                  title={item.title}
                >
                  {renderSidebarIcon(item.customIcon, item.icon, item.title)}
                  <span className={styles.title}>{item.title}</span>
                </NavLink>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </aside>
  );
});

Sidebar.displayName = "Sidebar";

export default Sidebar;
